import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core'
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { Venue } from '../domain/Venue'
import { Voucher } from '../domain/Voucher'
import { PricedSKU } from '../domain/SKU'
import { PaymentSession } from '../services/PaymentSession'
import { PaymentsService, SaleDeliveryMethod } from '../services/payments.service'
import { Sale } from '../domain/Sale'
import { environment } from '../../environments/environment'
import { RichTextModalComponent } from '../rich-text-modal/rich-text-modal.component'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { Address } from '../domain/Address'
import { DeliveryDetails } from '../domain/DeliveryDetails'
import { Subscription } from 'rxjs'

type CheckoutStep = 'DELIVERY' | 'PAYMENT'

@Component({
    selector: 'app-voucher-checkout',
    templateUrl: './voucher-checkout.component.html',
})
export class VoucherCheckoutComponent implements OnInit, OnChanges, OnDestroy {

    @Input() venue!: Venue
    @Input() voucher!: Voucher
    @Input() form!: FormGroup
    @Output() paymentConfirmed = new EventEmitter<Sale>()
    selectedSKU: PricedSKU | null = null
    selectedSKUVoucherAmount: number | undefined
    expiryDate: Date | null = null
    quantity = 1
    paymentSession: PaymentSession | null = null
    checkoutForm!: FormGroup
    deliveryMethod: SaleDeliveryMethod = 'EMAIL'
    postalDeliverySelected = false
    selectedPostalDeliveryFee = 0
    selectedTotalPrice: number | undefined
    deliveryStepActive = true
    paymentStepActive = false
    isRyftPayment = false
    isPaying = false
    walletPaymentAvailable = false
    cardDetailsAreComplete = false
    deliveryEmailAddressControl!: AbstractControl
    recipientNameControl!: AbstractControl
    addressLineOneControl!: AbstractControl
    cityControl!: AbstractControl
    postCodeControl!: AbstractControl
    countryControl!: AbstractControl
    billingEmailAddressControl!: AbstractControl
    agreedToTermsControl!: AbstractControl
    privacyPolicyURL = environment.privacyPolicyURL
    recipientMessageToolbarOptions = [
        ['bold', 'italic', 'underline', 'strike'],
        ['clean'],
    ]
    private sale: Sale | null = null
    private checkoutStep: CheckoutStep = 'DELIVERY'
    private deliveryMethodSubscription: Subscription | null = null
    private paymentSetupRequest = 0

    constructor(
        private fb: FormBuilder,
        private changeDetectorRef: ChangeDetectorRef,
        private modalService: NgbModal,
        private paymentsService: PaymentsService
    ) { }

    ngOnInit() {
        this.checkoutForm = this.makeCheckoutForm()
        this.bindCheckoutFormControls()
        this.updateDeliveryMethodState()
        this.updateCheckoutStepState()
        this.updateOrderSummary()
        this.updateDeliveryValidators()
        this.deliveryMethodSubscription = this.checkoutForm.get('deliveryMethod')?.valueChanges.subscribe(() => {
            this.updateDeliveryMethodState()
            this.updateOrderSummary()
            this.updateDeliveryValidators()
            this.resetPaymentSession()
        }) ?? null
    }

    ngOnChanges() {
        const skuId = this.form.get('skuId')?.value
        this.selectedSKU = this.voucher.getSKUWithId(skuId)
        this.selectedSKUVoucherAmount = this.selectedSKU
            ? this.voucher.getAmountForSKU(this.selectedSKU.id)
            : undefined
        this.quantity = this.getSelectedQuantity()
        this.expiryDate = this.voucher.getExpiryDateFromDate(new Date())
        if (this.checkoutForm && !this.voucher.postalDeliveryEnabled && this.deliveryMethod === 'POSTAL') {
            this.checkoutForm.get('deliveryMethod')?.setValue('EMAIL')
        }
        this.updateOrderSummary()
    }

    ngOnDestroy() {
        this.paymentSetupRequest += 1
        this.deliveryMethodSubscription?.unsubscribe()
        this.paymentSession?.destroy()
        this.paymentSession = null
    }

    showPolicy() {
        const modal = this.modalService.open(RichTextModalComponent)
        modal.componentInstance.title = 'Terms and Conditions'
        modal.componentInstance.content = this.voucher.policy
    }

    async goToPaymentStep() {
        this.markDeliveryDetailsAsTouched()
        this.checkoutForm.updateValueAndValidity()
        if (!this.voucher.isOnSale || !this.selectedSKU || this.form.invalid || this.deliveryDetailsInvalid()) {
            return
        }
        this.checkoutStep = 'PAYMENT'
        this.updateCheckoutStepState()
        this.changeDetectorRef.detectChanges()
        await Promise.resolve()
        this.mountSetUpPaymentDetailsElement(this.venue)
            .catch((error) => {
                console.error(error)
            })
    }

    goToDeliveryStep() {
        this.checkoutStep = 'DELIVERY'
        this.updateCheckoutStepState()
        this.resetPaymentSession()
    }

    async pay() {
        this.checkoutForm.markAllAsTouched()
        this.checkoutForm.updateValueAndValidity()
        if (!this.voucher.isOnSale || !this.selectedSKU || this.form.invalid || this.checkoutForm.invalid) {
            return
        }
        if (!this.paymentSession) {
            throw new Error('Payment session not set up')
        }
        const billingEmailAddress = this.checkoutForm.get('billingEmailAddress')?.value
        if (billingEmailAddress === null) {
            throw new Error('Billing email address not set up')
        }
        this.isPaying = true
        try {
            const sale = await this.saveSaleFulfilmentDetails()
            const payment = await this.paymentSession.confirm(billingEmailAddress)
            if (payment === undefined) {
                return
            }
            this.paymentConfirmed.emit(sale)
        } catch (error) {
            console.error(error)
        } finally {
            this.isPaying = false
        }
    }

    private markDeliveryDetailsAsTouched() {
        this.deliveryControlNames()
            .forEach(controlName => this.checkoutForm.get(controlName)?.markAsTouched())
    }

    private deliveryDetailsInvalid(): boolean {
        return this.deliveryControlNames()
            .some(controlName => this.checkoutForm.get(controlName)?.invalid)
            || this.checkoutForm.hasError('postalAddressIncomplete')
    }

    private deliveryControlNames(): string[] {
        const commonControls = [
            'deliveryMethod',
            'recipientName',
            'senderMessage',
        ]
        if (!this.postalDeliverySelected) {
            return [
                ...commonControls,
                'deliveryEmailAddress',
            ]
        }
        return [
            ...commonControls,
            'addressLineOne',
            'addressLineTwo',
            'city',
            'county',
            'postCode',
            'country',
        ]
    }

    private bindCheckoutFormControls() {
        this.deliveryEmailAddressControl = this.getCheckoutFormControl('deliveryEmailAddress')
        this.recipientNameControl = this.getCheckoutFormControl('recipientName')
        this.addressLineOneControl = this.getCheckoutFormControl('addressLineOne')
        this.cityControl = this.getCheckoutFormControl('city')
        this.postCodeControl = this.getCheckoutFormControl('postCode')
        this.countryControl = this.getCheckoutFormControl('country')
        this.billingEmailAddressControl = this.getCheckoutFormControl('billingEmailAddress')
        this.agreedToTermsControl = this.getCheckoutFormControl('agreedToTerms')
    }

    private getCheckoutFormControl(controlName: string): AbstractControl {
        const control = this.checkoutForm.get(controlName)
        if (!control) {
            throw new Error(`Checkout form control '${controlName}' not found`)
        }
        return control
    }

    private updateDeliveryMethodState() {
        const method = this.checkoutForm.get('deliveryMethod')?.value
        this.deliveryMethod = method === 'POSTAL' ? 'POSTAL' : 'EMAIL'
        this.postalDeliverySelected = this.deliveryMethod === 'POSTAL'
    }

    private updateCheckoutStepState() {
        this.deliveryStepActive = this.checkoutStep === 'DELIVERY'
        this.paymentStepActive = this.checkoutStep === 'PAYMENT'
    }

    private updateOrderSummary() {
        this.selectedPostalDeliveryFee = this.postalDeliverySelected
            ? this.voucher.postalDeliveryFee ?? 0
            : 0
        this.selectedTotalPrice = this.selectedSKU
            ? (this.selectedSKU.price * this.quantity) + this.selectedPostalDeliveryFee
            : undefined
    }

    private async mountSetUpPaymentDetailsElement(
        venue: Venue
    ) {
        if (this.selectedSKU === null) {
            throw new Error('Selected SKU not set up')
        }
        const requestId = ++this.paymentSetupRequest
        this.paymentSession?.destroy()
        this.paymentSession = null
        this.sale = null
        this.cardDetailsAreComplete = false
        this.walletPaymentAvailable = false
        const response = await this.paymentsService.makeSalePaymentIntent(
            venue,
            this.voucher.id,
            this.selectedSKU.id,
            this.getSelectedQuantity(),
            this.deliveryMethod
        )
        if (requestId !== this.paymentSetupRequest) {
            return
        }
        this.sale = response.sale
        this.cardDetailsAreComplete = false
        this.changeDetectorRef.detectChanges()
        await Promise.resolve()
        const paymentSession = await this.paymentsService.createPaymentSession(venue, {
            intent: response.paymentIntent,
            usage: 'payment',
            merchantName: venue.voucherDisplayName(),
            cardFormSelector: '#payment-card-form',
            applePayWalletButtonSelector: '#payment-apple-wallet-button',
            googlePayWalletButtonSelector: '#payment-google-wallet-button',
        }, 'VoucherPayments')
        if (requestId !== this.paymentSetupRequest) {
            paymentSession.destroy()
            return
        }
        this.paymentSession = paymentSession
        this.isRyftPayment = paymentSession.gateway === 'ryftpay'
        this.walletPaymentAvailable = this.paymentSession.walletPaymentAvailable
        this.changeDetectorRef.detectChanges()
        this.paymentSession.setOnValidityChange((isValid) => this.updateCardValidationState(isValid))
        this.paymentSession.setOnWalletConfirmed(() => {
            void this.onWalletConfirmed()
        })
        this.paymentSession.setOnError(() => {
            this.cardDetailsAreComplete = false
            this.changeDetectorRef.detectChanges()
        })
    }

    private async onWalletConfirmed() {
        this.checkoutForm.markAllAsTouched()
        this.checkoutForm.updateValueAndValidity()
        if (this.form.invalid || this.checkoutForm.invalid) {
            return
        }
        this.isPaying = true
        this.changeDetectorRef.detectChanges()
        try {
            const sale = await this.saveSaleFulfilmentDetails()
            this.paymentConfirmed.emit(sale)
        } catch (error) {
            console.error(error)
        } finally {
            this.isPaying = false
            this.changeDetectorRef.detectChanges()
        }
    }

    private resetPaymentSession() {
        this.paymentSetupRequest += 1
        this.paymentSession?.destroy()
        this.paymentSession = null
        this.isRyftPayment = false
        this.sale = null
        this.cardDetailsAreComplete = false
        this.walletPaymentAvailable = false
    }

    private updateCardValidationState(isComplete: boolean) {
        this.cardDetailsAreComplete = isComplete
        this.changeDetectorRef.detectChanges()
    }

    private makeCheckoutForm() {
        return this.fb.group({
            billingEmailAddress: [null, [Validators.required, Validators.email]],
            deliveryMethod: ['EMAIL' as SaleDeliveryMethod],
            deliveryEmailAddress: [null, [Validators.required, Validators.email]],
            recipientName: [null],
            addressLineOne: [null],
            addressLineTwo: [null],
            city: [null],
            county: [null],
            postCode: [null],
            country: [null],
            senderMessage: [null],
            agreedToTerms: [false, [Validators.requiredTrue]],
        }, {
            validators: [this.postalAddressUsefulValidator()],
        })
    }

    private makeDeliveryDetails(): DeliveryDetails {
        const recipientName = this.normaliseOptionalText(this.checkoutForm.get('recipientName')?.value)
        if (!this.postalDeliverySelected) {
            return new DeliveryDetails(
                this.normaliseOptionalText(this.checkoutForm.get('deliveryEmailAddress')?.value),
                recipientName,
                null
            )
        }
        return new DeliveryDetails(
            null,
            recipientName,
            new Address(
                this.normaliseRequiredText(this.checkoutForm.get('addressLineOne')?.value),
                this.normaliseOptionalText(this.checkoutForm.get('addressLineTwo')?.value),
                this.normaliseRequiredText(this.checkoutForm.get('city')?.value),
                this.normaliseOptionalText(this.checkoutForm.get('county')?.value),
                this.normaliseRequiredText(this.checkoutForm.get('postCode')?.value).toUpperCase(),
                this.normaliseRequiredText(this.checkoutForm.get('country')?.value)
            )
        )
    }

    private async saveSaleFulfilmentDetails(): Promise<Sale> {
        if (this.sale === null) {
            throw new Error('Sale not set up')
        }
        const senderMessage = this.checkoutForm.get('senderMessage')?.value
        this.sale = await this.paymentsService.setSaleFulfilmentDetails(
            this.venue.id,
            this.sale.id,
            this.makeDeliveryDetails(),
            senderMessage
        )
        return this.sale
    }

    private updateDeliveryValidators() {
        const deliveryEmailControl = this.checkoutForm.get('deliveryEmailAddress')
        const recipientNameControl = this.checkoutForm.get('recipientName')
        if (this.postalDeliverySelected) {
            deliveryEmailControl?.clearValidators()
            recipientNameControl?.setValidators([
                Validators.required,
                this.trimmedMinLengthValidator(2),
                this.trimmedMaxLengthValidator(120),
            ])
            this.setPostalAddressControlValidators(true)
        } else {
            deliveryEmailControl?.setValidators([Validators.required, Validators.email])
            recipientNameControl?.setValidators([this.trimmedMaxLengthValidator(120)])
            this.setPostalAddressControlValidators(false)
        }
        [
            deliveryEmailControl,
            recipientNameControl,
            this.checkoutForm.get('addressLineOne'),
            this.checkoutForm.get('addressLineTwo'),
            this.checkoutForm.get('city'),
            this.checkoutForm.get('county'),
            this.checkoutForm.get('postCode'),
            this.checkoutForm.get('country'),
        ].forEach(control => control?.updateValueAndValidity({ emitEvent: false }))
        this.checkoutForm.updateValueAndValidity({ emitEvent: false })
    }

    private setPostalAddressControlValidators(required: boolean) {
        if (!required) {
            this.postalAddressControlNames()
                .forEach(controlName => this.checkoutForm.get(controlName)?.clearValidators())
            return
        }
        this.setAddressValidators('addressLineOne', required ? 3 : null)
        this.setAddressValidators('addressLineTwo', null)
        this.setAddressValidators('city', required ? 2 : null)
        this.setAddressValidators('county', null)
        this.setAddressValidators('postCode', required ? 2 : null)
        this.setAddressValidators('country', required ? 2 : null)
    }

    private postalAddressControlNames(): string[] {
        return [
            'addressLineOne',
            'addressLineTwo',
            'city',
            'county',
            'postCode',
            'country',
        ]
    }

    private setAddressValidators(controlName: string, minimumLength: number | null) {
        const validators: ValidatorFn[] = [this.trimmedMaxLengthValidator(120)]
        if (minimumLength !== null) {
            validators.unshift(Validators.required, this.trimmedMinLengthValidator(minimumLength))
        }
        this.checkoutForm.get(controlName)?.setValidators(validators)
    }

    private trimmedMinLengthValidator(minimumLength: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = this.normaliseOptionalText(control.value)
            if (value === null || value.length >= minimumLength) {
                return null
            }
            return { minlength: { requiredLength: minimumLength, actualLength: value.length } }
        }
    }

    private trimmedMaxLengthValidator(maximumLength: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = this.normaliseOptionalText(control.value)
            if (value === null || value.length <= maximumLength) {
                return null
            }
            return { maxlength: { requiredLength: maximumLength, actualLength: value.length } }
        }
    }

    private postalAddressUsefulValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (control.get('deliveryMethod')?.value !== 'POSTAL') {
                return null
            }
            const usefulAddressText = [
                'addressLineOne',
                'addressLineTwo',
                'city',
                'county',
                'postCode',
                'country',
            ]
                .map(controlName => this.normaliseOptionalText(control.get(controlName)?.value))
                .filter((value): value is string => value !== null)
                .join('')
            return usefulAddressText.length >= 12 ? null : { postalAddressIncomplete: true }
        }
    }

    private normaliseOptionalText(value: unknown): string | null {
        if (typeof value !== 'string') {
            return null
        }
        const normalised = value.trim().replace(/\s+/g, ' ')
        return normalised.length > 0 ? normalised : null
    }

    private normaliseRequiredText(value: unknown): string {
        return this.normaliseOptionalText(value) ?? ''
    }

    private getSelectedQuantity(): number {
        const quantity = Number(this.form.get('quantity')?.value ?? 1)
        return Number.isInteger(quantity) && quantity > 0 ? quantity : 1
    }
}
